import { useMemo } from "react";
import * as THREE from "three";

/**
 * Course-local dressing around the purchased six-metre beach-bar shell.
 * The GLB contains the building and furniture; this supplies the wider resort
 * context and Elena's physical table/iPad so the scene does not end at the
 * model's small authored ground slab.
 */
export function BeachSurround({
  ipadScreen = "teams",
  notice,
  drink = "cup",
}: {
  ipadScreen?: "teams" | "home" | "notify";
  /** ipadScreen "notify": lock-screen notification text. */
  notice?: { time: string; app: string; from: string; text: string };
  drink?: "cup" | "cocktail";
}) {
  const sand = useMemo(() => {
    const size = 256;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    const image = ctx.createImageData(size, size);
    for (let i = 0; i < image.data.length; i += 4) {
      const grain = 207 + Math.floor(Math.random() * 27);
      image.data[i] = Math.min(255, grain + 20);
      image.data[i + 1] = Math.min(255, grain + 5);
      image.data[i + 2] = Math.max(0, grain - 28);
      image.data[i + 3] = 255;
    }
    ctx.putImageData(image, 0, 0);
    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(22, 22);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = 4;
    return texture;
  }, []);
  const teamsScreen = useMemo(() => {
    const canvas = document.createElement("canvas");
    canvas.width = 768;
    canvas.height = 466;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    if (ipadScreen === "notify") {
      const g = ctx.createLinearGradient(0, 0, 0, 466);
      g.addColorStop(0, "#24435a");
      g.addColorStop(1, "#0f1d2a");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, 768, 466);
      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "center";
      ctx.font = "300 96px sans-serif";
      ctx.fillText(notice?.time ?? "", 384, 140);
      ctx.fillStyle = "rgba(245,245,247,0.9)";
      ctx.beginPath();
      ctx.roundRect(84, 210, 600, 128, 26);
      ctx.fill();
      ctx.textAlign = "left";
      ctx.fillStyle = "#4a154b";
      ctx.beginPath();
      ctx.roundRect(108, 236, 64, 64, 14);
      ctx.fill();
      ctx.fillStyle = "#111111";
      ctx.font = "700 28px sans-serif";
      ctx.fillText(`${notice?.app ?? ""} · ${notice?.from ?? ""}`, 196, 262);
      ctx.font = "26px sans-serif";
      ctx.fillText((notice?.text ?? "").slice(0, 34), 196, 304);
    } else if (ipadScreen === "home") {
      ctx.fillStyle = "#101924";
      ctx.fillRect(0, 0, 768, 466);
      ctx.fillStyle = "#d7e4e2";
      ctx.font = "600 28px sans-serif";
      ctx.fillText("Elena's company iPad", 40, 52);
      ctx.fillStyle = "#8ff0dc";
      ctx.font = "700 18px sans-serif";
      ctx.fillText("17:12", 680, 52);
      const apps = ["Mail", "Browser", "Teams", "Files", "Calendar", "Real or not?"];
      apps.forEach((label, index) => {
        const col = index % 3;
        const row = Math.floor(index / 3);
        const x = 70 + col * 220;
        const y = 110 + row * 160;
        ctx.fillStyle = label.startsWith("Real") ? "#1a6d62" : "#163038";
        ctx.beginPath();
        ctx.roundRect(x, y, 88, 88, 18);
        ctx.fill();
        ctx.fillStyle = "#d7fff6";
        ctx.font = "700 22px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(label.startsWith("Real") ? "AI?" : label.slice(0, 2), x + 44, y + 52);
        ctx.fillStyle = "#cfe8d1";
        ctx.font = "600 18px sans-serif";
        ctx.fillText(label, x + 44, y + 118);
        ctx.textAlign = "left";
      });
    } else {
      const gradient = ctx.createLinearGradient(0, 0, 768, 466);
      gradient.addColorStop(0, "#171b25");
      gradient.addColorStop(1, "#26334d");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 768, 466);
      ctx.fillStyle = "#5969dd";
      ctx.beginPath();
      ctx.roundRect(52, 65, 112, 112, 22);
      ctx.fill();
      ctx.fillStyle = "#ffffff";
      ctx.font = "700 62px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("T", 108, 143);
      ctx.textAlign = "left";
      ctx.font = "700 34px sans-serif";
      ctx.fillText("Incoming Teams call", 200, 104);
      ctx.font = "700 46px sans-serif";
      ctx.fillText("Marc Weber — CFO", 200, 161);
      ctx.fillStyle = "#b9c4d7";
      ctx.font = "28px sans-serif";
      ctx.fillText("Project Horizon · Confidential", 52, 246);
      ctx.fillStyle = "#59c9b5";
      ctx.beginPath();
      ctx.roundRect(52, 300, 664, 92, 46);
      ctx.fill();
      ctx.fillStyle = "#071713";
      ctx.font = "700 31px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("Open iPad to answer", 384, 358);
    }
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    return texture;
  }, [ipadScreen, notice?.time, notice?.app, notice?.from, notice?.text]);

  const palms = [
    [-13, -5, 0.16],
    [-9, 5, -0.1],
    [10, 4, 0.12],
    [14, -7, -0.18],
  ] as const;

  return (
    <group>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.16, 1]} receiveShadow>
        <planeGeometry args={[72, 58, 1, 1]} />
        <meshStandardMaterial map={sand ?? undefined} color="#d9bd7e" roughness={0.96} />
      </mesh>

      {/* Low dunes keep the horizon from reading as an infinite flat card. */}
      {[-18, -11, 9, 17].map((x, i) => (
        <mesh key={x} position={[x, -0.05, 8 + (i % 2) * 3]} scale={[5.8, 0.55, 2.2]} receiveShadow>
          <sphereGeometry args={[1, 24, 12]} />
          <meshStandardMaterial color={i % 2 ? "#caae71" : "#d4b97a"} roughness={1} />
        </mesh>
      ))}

      {palms.map(([x, z, lean], i) => (
        <group key={`${x}-${z}`} position={[x, 0, z]} rotation={[lean, 0, i % 2 ? 0.08 : -0.08]}>
          <mesh position={[0, 2.2, 0]} castShadow>
            <cylinderGeometry args={[0.12, 0.22, 4.5, 9]} />
            <meshStandardMaterial color="#70502f" roughness={0.92} />
          </mesh>
          <group position={[0, 4.35, 0]}>
            {[0, 1, 2, 3, 4, 5].map((n) => (
              <mesh
                key={n}
                position={[Math.cos((n * Math.PI) / 3) * 0.85, -0.12, Math.sin((n * Math.PI) / 3) * 0.85]}
                rotation={[0.12, -(n * Math.PI) / 3, 0.2]}
                castShadow
              >
                <coneGeometry args={[0.62, 2.25, 5]} />
                <meshStandardMaterial color={n % 2 ? "#2f7845" : "#3a8a50"} roughness={0.88} />
              </mesh>
            ))}
          </group>
        </group>
      ))}

      {/* Elena is seated at the actual front counter. Keep the fascia below
          the counter top so it cannot hide the iPad at zoom. */}
      <group position={[0, 0, -1.15]}>
        <mesh position={[0, 0.44, 0.24]} castShadow receiveShadow>
          <boxGeometry args={[4.5, 0.88, 0.62]} />
          <meshStandardMaterial color="#654735" roughness={0.76} />
        </mesh>
        <mesh position={[0, 1.11, 0]} castShadow receiveShadow>
          <boxGeometry args={[4.9, 0.12, 1.06]} />
          <meshStandardMaterial color="#9a7657" roughness={0.58} />
        </mesh>
        <group position={[-1.15, 1.4, -0.42]} rotation={[-0.14, Math.PI, 0]}>
          <mesh castShadow>
            <boxGeometry args={[0.7, 0.45, 0.035]} />
            <meshStandardMaterial color="#1a1d24" roughness={0.28} metalness={0.72} />
          </mesh>
          <mesh position={[0, 0, 0.019]}>
            <planeGeometry args={[0.64, 0.39]} />
            <meshBasicMaterial map={teamsScreen ?? undefined} color={teamsScreen ? "#ffffff" : "#5969dd"} toneMapped={false} />
          </mesh>
        </group>
        {drink === "cocktail" && <BeachCocktail position={[-0.42, 1.17, -0.36]} />}
        {drink !== "cocktail" && (
        <>
        <mesh position={[0.65, 1.24, -0.05]} castShadow>
          <cylinderGeometry args={[0.08, 0.065, 0.2, 20]} />
          <meshStandardMaterial color="#e8f2f1" roughness={0.35} />
        </mesh>
        <mesh position={[0.65, 1.35, -0.05]} rotation={[-Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.095, 0.015, 8, 24]} />
          <meshStandardMaterial color="#78b8b6" roughness={0.4} />
        </mesh>
        </>
        )}
      </group>

    </group>
  );
}

/** A tall glass with an orange-to-red drink, ice, a straw and a lime wheel.
 *  `position` is the base of the glass on the counter top. */
function BeachCocktail({ position }: { position: [number, number, number] }) {
  return (
    <group position={position}>
      <mesh position={[0, 0.085, 0]} castShadow>
        <cylinderGeometry args={[0.048, 0.04, 0.17, 28, 1, true]} />
        <meshPhysicalMaterial color="#eef7fb" transparent opacity={0.22} roughness={0.05} depthWrite={false} side={THREE.DoubleSide} />
      </mesh>
      <mesh position={[0, 0.006, 0]}>
        <cylinderGeometry args={[0.04, 0.04, 0.012, 28]} />
        <meshPhysicalMaterial color="#ffffff" transparent opacity={0.5} roughness={0.05} />
      </mesh>
      <mesh position={[0, 0.05, 0]}>
        <cylinderGeometry args={[0.043, 0.038, 0.085, 28]} />
        <meshStandardMaterial color="#e2412a" roughness={0.3} emissive="#5a1206" emissiveIntensity={0.35} />
      </mesh>
      <mesh position={[0, 0.115, 0]}>
        <cylinderGeometry args={[0.046, 0.043, 0.045, 28]} />
        <meshStandardMaterial color="#f59a22" roughness={0.3} emissive="#6a3a05" emissiveIntensity={0.35} />
      </mesh>
      {[[0.014, 0.135, 0.01], [-0.016, 0.128, -0.008], [0.004, 0.142, -0.018]].map((p, i) => (
        <mesh key={i} position={p as [number, number, number]} rotation={[0.4 * i, 0.7 * i, 0.2]}>
          <boxGeometry args={[0.024, 0.024, 0.024]} />
          <meshPhysicalMaterial color="#f4fbff" transparent opacity={0.55} roughness={0.08} />
        </mesh>
      ))}
      <mesh position={[0.016, 0.16, 0.004]} rotation={[0, 0, -0.22]}>
        <cylinderGeometry args={[0.0035, 0.0035, 0.2, 10]} />
        <meshStandardMaterial color="#1f9d8a" roughness={0.4} />
      </mesh>
      <mesh position={[-0.045, 0.165, 0]} rotation={[0, 0, Math.PI / 2 - 0.25]}>
        <cylinderGeometry args={[0.03, 0.03, 0.008, 24]} />
        <meshStandardMaterial color="#9ccc3a" roughness={0.5} />
      </mesh>
    </group>
  );
}
